import { datadogRum, RumEvent } from '@datadog/browser-rum'
import { DatadogInitOptions, DatadogGlobalContext } from './types'

const isCanceledErrorMessage = (message: string): boolean => {
  const normalized = message.toLowerCase()
  return (
    normalized.includes('canceled') ||
    normalized.includes('cancelled') ||
    normalized.includes('err_canceled') ||
    normalized.includes('cancelederror')
  )
}

const isNetworkErrorMessage = (message: string): boolean => {
  const normalized = message.toLowerCase()
  return (
    normalized.includes('network error') ||
    normalized.includes('err_network') ||
    normalized.includes('econnaborted')
  )
}

const isMsalTokenRefreshMessage = (message: string): boolean =>
  message
    .toLowerCase()
    .includes('access token expired, triggering msal refresh')

/**
 * Benign "ResizeObserver loop completed with undelivered notifications" /
 * "ResizeObserver loop limit exceeded" browser warning surfaced on split-panel
 * / resizable pages. No functional impact — pure RUM noise (issue 019).
 */
const isResizeObserverLoopMessage = (message: string): boolean =>
  message.toLowerCase().includes('resizeobserver loop')

/**
 * `focus-trap-react` teardown race — a document listener reads `tabbableNodes`
 * on an already-deactivated trap while a modal/panel unmounts. Library-internal,
 * fires during teardown, does not break the user flow (issue 010).
 */
const isFocusTrapTeardownMessage = (message: string): boolean =>
  message.toLowerCase().includes('tabbablenodes')

/**
 * Drop aborted / offline Axios noise, expected MSAL refresh logs, and benign
 * browser / library teardown warnings from RUM (issues 004, 009, 010, 019).
 * Real online Network Errors and failed refresh still report.
 */
export const shouldDropRumErrorEvent = (event: RumEvent): boolean => {
  if (event.type !== 'error') {
    return false
  }

  const message = event.error?.message ?? ''
  if (!message) {
    return false
  }

  if (isCanceledErrorMessage(message)) {
    return true
  }

  if (isMsalTokenRefreshMessage(message)) {
    return true
  }

  if (isResizeObserverLoopMessage(message)) {
    return true
  }

  if (isFocusTrapTeardownMessage(message)) {
    return true
  }

  const online =
    typeof navigator === 'undefined' ? true : navigator.onLine !== false

  return !online && isNetworkErrorMessage(message)
}

/**
 * Initialize Datadog RUM with provided configuration
 * @param options - Datadog initialization options
 * @param globalContext - Optional global context to attach to required events (mainly for hcp as microsite and hcp are in same ecrm-ui)
 */
export const initDatadog = (
  options: DatadogInitOptions,
  globalContext?: DatadogGlobalContext
) => {
  datadogRum.init({
    applicationId: options.applicationId,
    clientToken: options.clientToken,
    site: options.site ?? 'us5.datadoghq.com',
    service: options.service,
    env: options.env,
    version: options.version ?? '6.27.0',
    sessionSampleRate: options.sessionSampleRate ?? 100,
    sessionReplaySampleRate: 0,
    trackBfcacheViews: options.trackBfcacheViews ?? true,
    defaultPrivacyLevel: options.defaultPrivacyLevel ?? 'mask-user-input',
    trackingConsent: options.trackingConsent ?? 'granted',
    storeContextsAcrossPages: options.storeContextsAcrossPages ?? true,
    trackSessionAcrossSubdomains: options.trackSessionAcrossSubdomains ?? true,
    beforeSend: (event) => !shouldDropRumErrorEvent(event),
  })

  if (globalContext) {
    datadogRum.setGlobalContext(globalContext)
  }
}

export const stopDatadog = () => {
  datadogRum.stopSession()
}

export const setDatadogContext = (context: DatadogGlobalContext) => {
  datadogRum.setGlobalContext(context)
}
